import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

interface TopBarProps {
  pageTitle: string;
  sidebarWidth: number;
  onQuickNav?: (page: string) => void;
  isMobile?: boolean;
  onHamburgerClick?: () => void;
  children?: React.ReactNode;
}

const TopBar: React.FC<TopBarProps> = ({ pageTitle, sidebarWidth, isMobile = false, onHamburgerClick, children }) => {
  const { user } = useAuth();
  const { theme, themeName, setTheme, themes, bgMode, bgModeName, setBgMode, bgModes } = useTheme();
  const [time, setTime] = useState(new Date());
  const [showThemes, setShowThemes] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{
      height: 48,
      background: 'rgba(var(--card-bg-rgb), 0.7)',
      backdropFilter: 'blur(12px) saturate(180%)',
      WebkitBackdropFilter: 'blur(12px) saturate(180%)',
      boxShadow: '0 2px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isMobile ? '0 12px' : '0 24px',
      position: 'fixed',
      // Matched offset with the sidebar so they share the same top
      // gap and feel like one cohesive frame (May 17, 2026).
      top: isMobile ? 0 : 8,
      left: isMobile ? 0 : sidebarWidth,
      right: 0,
      zIndex: 999,
      transition: 'left 0.2s ease, background-color 0.3s ease',
      gap: 8,
    }}>
      {/* Left Section: Hamburger + Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: '0 1 auto' }}>
        {/* Hamburger button for mobile */}
        {isMobile && (
          <button
            onClick={onHamburgerClick}
            style={{
              width: 36, height: 36, borderRadius: 8,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-secondary)', cursor: 'pointer', padding: 0,
              flexShrink: 0,
            }}
          >
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <line x1={3} y1={6} x2={21} y2={6} />
              <line x1={3} y1={12} x2={21} y2={12} />
              <line x1={3} y1={18} x2={21} y2={18} />
            </svg>
          </button>
        )}

        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>HALA</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>/</span>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: theme.accent, flexShrink: 0,
            boxShadow: `0 0 8px ${theme.accent}66`,
          }} />
          <span style={{
            color: 'var(--text)', fontSize: isMobile ? 14 : 15, fontWeight: 700,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{pageTitle}</span>
        </div>
      </div>

      {/* Center Section: Search — triggers Global Search (Ctrl+K) */}
      {!isMobile && (
        <div
          onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }))}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 12px',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(8px)',
            borderRadius: 20,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: 200,
            marginLeft: 24,
            flex: '0 1 auto',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)'; }}
        >
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth={2}>
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <span style={{ flex: 1, color: 'var(--text-muted)', fontSize: 13 }}>Search...</span>
          <kbd style={{
            fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
            color: 'var(--text-muted)', fontFamily: 'inherit',
          }}>⌘K</kbd>
        </div>
      )}

      {/* Right Section: Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 14, flexShrink: 0 }}>
        {/* Time Display — hidden on very small screens */}
        {!isMobile && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(8px)',
            borderRadius: 16,
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth={2}>
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}>
              {time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              {' '}
              {time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        )}

        {/* Notification Center (passed as children) */}
        {children}

        {/* Theme Picker */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowThemes(!showThemes)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 12px',
              borderRadius: 20,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(8px)',
              fontSize: 11, color: 'var(--text-secondary)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255, 255, 255, 0.08)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255, 255, 255, 0.15)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255, 255, 255, 0.05)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255, 255, 255, 0.1)';
            }}
          >
            <div style={{
              width: 10, height: 10, borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentHover})`,
              boxShadow: `0 0 12px ${theme.accent}88`,
            }} />
            {!isMobile && <span>Theme</span>}
            <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{
              transition: 'transform 0.3s ease',
              transform: showThemes ? 'rotate(180deg)' : 'rotate(0deg)',
            }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showThemes && (
            <>
              <div onClick={() => setShowThemes(false)} style={{ position: 'fixed', inset: 0, zIndex: 1000 }} />
              <div style={{
                position: 'absolute', top: 38, right: 0,
                background: 'rgba(var(--card-bg-rgb), 0.8)',
                backdropFilter: 'blur(16px) saturate(180%)',
                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: 16,
                padding: 16, width: isMobile ? 220 : 240, zIndex: 1001,
                boxShadow: '0 16px 48px rgba(0,0,0,0.16), 0 4px 12px rgba(0,0,0,0.08)',
                animation: 'slideDown 0.3s ease',
              }}>
                {/* Background Mode Section */}
                <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, marginBottom: 10, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  Background
                </div>
                {/* Grid (not flex) so 5 tiles share the panel width
                    cleanly. With flex: 1 each tile would refuse to
                    shrink below its label width and the last tile
                    (Warm) would bleed past the panel's right edge. */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                  gap: 6,
                  marginBottom: 16,
                }}>
                  {Object.entries(bgModes).map(([key, m]) => {
                    const active = bgModeName === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setBgMode(key)}
                        title={m.name}
                        style={{
                          minWidth: 0,
                          padding: '8px 2px',
                          borderRadius: 10,
                          border: active ? `2px solid ${theme.accent}` : '2px solid rgba(255, 255, 255, 0.1)',
                          background: m.bg,
                          cursor: 'pointer',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                          transition: 'all 0.2s ease',
                          boxShadow: active ? `0 4px 16px ${theme.accent}44` : 'inset 0 1px 2px rgba(255, 255, 255, 0.1)',
                          overflow: 'hidden',
                        }}
                      >
                        <span style={{ fontSize: 14, lineHeight: 1 }}>{m.icon}</span>
                        <span style={{
                          fontSize: 8, fontWeight: 600,
                          color: m.text,
                          letterSpacing: 0.2,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>{m.name}</span>
                      </button>
                    );
                  })}
                </div>

                <div style={{ height: 1, background: 'rgba(255, 255, 255, 0.1)', margin: '0 0 16px' }} />

                <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, marginBottom: 10, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  Accent Color
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                  {Object.entries(themes).map(([key, t]) => (
                    <button
                      key={key}
                      onClick={() => { setTheme(key); }}
                      title={t.name}
                      style={{
                        position: 'relative',
                        width: '100%', height: 40,
                        borderRadius: 12,
                        background: `linear-gradient(135deg, ${t.accent}, ${t.accentHover})`,
                        border: themeName === key ? `2px solid var(--text)` : '2px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: themeName === key ? 1 : 0.6,
                        boxShadow: themeName === key ? `0 4px 16px ${t.accent}55` : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      onMouseEnter={(e) => {
                        if (themeName !== key) {
                          (e.currentTarget as HTMLButtonElement).style.opacity = '0.8';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (themeName !== key) {
                          (e.currentTarget as HTMLButtonElement).style.opacity = '0.6';
                        }
                      }}
                    >
                      {themeName === key && (
                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3}>
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>

                <div style={{
                  marginTop: 16, padding: '10px 12px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  fontSize: 11, color: 'var(--text-muted)',
                }}>
                  <span>{bgMode.name} + {theme.name}</span>
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentHover})`,
                    border: '1.5px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: `0 0 8px ${theme.accent}66`,
                  }} />
                </div>

                <style>{`
                  @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-8px); }
                    to { opacity: 1; transform: translateY(0); }
                  }
                `}</style>
              </div>
            </>
          )}
        </div>

        {/* Online Status — hidden on mobile */}
        {!isMobile && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px',
            background: 'rgba(22, 163, 74, 0.1)',
            border: '1px solid rgba(22, 163, 74, 0.2)',
            borderRadius: 16,
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%',
              background: '#16a34a',
              boxShadow: '0 0 8px #16a34a88',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            }} />
            <span style={{ color: '#16a34a', fontSize: 11, fontWeight: 500 }}>Online</span>
            <style>{`
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
              }
            `}</style>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopBar;
